import { CommonModule } from '@angular/common';
import { Component, CUSTOM_ELEMENTS_SCHEMA, OnInit, inject } from '@angular/core';
import { ReactiveFormsModule, FormBuilder, Validators } from '@angular/forms';
import { MasterDataService } from '../../services/master-data.service';
import { ToastrService } from '../../services/toastr-service';
import {
  BehaviorSubject,
  EMPTY,
  Observable,
  catchError,
  combineLatest,
  debounceTime,
  lastValueFrom,
  map,
  of,
  shareReplay,
  startWith,
  switchMap,
  tap,
} from 'rxjs';
import { NgxSkeletonLoaderModule } from 'ngx-skeleton-loader';

import '@maersk-global/mds-components-core/mc-input';
import '@maersk-global/mds-components-core/mc-select';
import '@maersk-global/mds-components-core/mc-typeahead';
import '@maersk-global/mds-components-core/mc-option';
import '@maersk-global/mds-components-core/mc-checkbox';
import '@maersk-global/mds-components-core/mc-table';
import '@maersk-global/mds-components-core/mc-pagination';
import { TableColumn } from '@maersk-global/mds-components-core/mc-table/types';
import { IsoWeekDateRangeHelper } from './isoWeekDateRangeHelper';
import { AuthHelper } from '../../auth/auth-helper';
import { Roles } from '../../auth/Roles';
import { utils } from '../../helper/utils';
import '@maersk-global/mds-components-core/mc-tag';
import '@maersk-global/mds-components-core/mc-button';
import { downloadBlob, generateDateFilename } from '../../helper/download-helpers';
import { DownloadFilterCriteria, toDateBetweenFilters, toEqualsFilter } from '../../models/download-filter.model';
import { MasterDataManagementService } from '../../services/master-data-management.service';
import { EquipmentImportDetail } from '../../models/equipment-import-detail.model';
import { EquipmentImportDetailsRequest } from '../../models/equipment-import-request.model';

/** Category filter value → the label shown in the filter tags and the grid. */
const CATEGORY_LABELS = { sales: 'Sales', redelivery: 'Lease expired', normal: 'Normal' } as const;
type EquipmentCategory = keyof typeof CATEGORY_LABELS;
type EquipmentCategoryLabel = (typeof CATEGORY_LABELS)[EquipmentCategory];

@Component({
  selector: 'app-in-advance-container-visibility',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule, NgxSkeletonLoaderModule],
  templateUrl: './in-advance-container-visibility.component.html',
  styleUrls: ['./in-advance-container-visibility.component.scss'],
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
})
export class InAdvanceContainerVisibilityComponent implements OnInit {
  private static readonly INITIAL_RENDERED_LOCATION_OPTIONS = 200;
  // The API rejects requests with Limit outside 1-100 ("limit must be between 1 and 100").
  private static readonly MAX_API_PAGE_LIMIT = 100;
  private categoryCache: { key: string; rows: EquipmentImportDetail[]; loadedApiPages: number; apiTotalCount: number } | null = null;
  private readonly formBuilder = inject(FormBuilder);
  private readonly masterDataService = inject(MasterDataService);
  private readonly masterDataManagementService = inject(MasterDataManagementService);
  private readonly toaster = inject(ToastrService);
  isoWeekDateRangeHelper = new IsoWeekDateRangeHelper();
  private readonly userRoles = AuthHelper.user?.roles.map((role) => role.toLowerCase()) ?? [];
  isShopUser = this.userRoles.includes(Roles.SHOP);
  showShopLocationDropdown = !this.isShopUser;
  maintenanceShops = AuthHelper.user?.maintenanceShops ?? [];
  private readonly userShopLocations = this.maintenanceShops
    .reduce((acc, shop) => {
      const normalizedLocationCode = this.normalizeLocationCode(shop.locationCode);
      if (normalizedLocationCode && !acc.includes(normalizedLocationCode)) {
        acc.push(normalizedLocationCode);
      }
      return acc;
    }, [] as string[])
    .sort((a, b) => a.localeCompare(b));
  shopLocations = [...this.userShopLocations];
  visibleShopLocations: { label: string; value: string }[] = [];
  locationSearchTerm = '';
  private readonly initialShopLocationCode = this.isShopUser ? (this.shopLocations[0] ?? null) : null;
  totalPagesCount = 1;
  loading = false;
  filterTags: { key: string; value: string }[] = [];
  isDownloadingData = false;
  currentSortDirection = '';
  currentSortColumn = '';

  filterForm = this.formBuilder.group({
    shopLocationCode: [{ value: this.initialShopLocationCode, disabled: this.isShopUser }],
    containerNumber: [null, [Validators.pattern(/^[A-Z]{4}\d{6,9}$/)]],
    grade: [null],
    size: [null],
    type: [null],
    category: [null as EquipmentCategory | null],
    currentWeek: [true],
    week1: [false],
    week2: [false],
  });

  columns: TableColumn[] = [
    { id: 'equipmentNumber', label: 'Container Number', sortDisabled: true },
    { id: 'category', label: 'Category', sortDisabled: true },
    { id: 'equipmentSize', label: 'Size', sortDisabled: true },
    { id: 'equipmentType', label: 'Type', sortDisabled: true },
    { id: 'gradeCode', label: 'Grade', sortDisabled: true },
    {
      id: 'estimatedTimeOfArrivalDepot',
      label: 'Estimated Arrival Date',
      description: 'Calculated as ETA + 7 days',
      descriptionWidth: 100,
      dataType: {
        type: 'string',
        formatter: (value) => {
          const stringValue = value as string;
          const date = new Date(stringValue);
          return stringValue && date ? utils.dateFormatter.format(date) : '';
        },
      },
    },
  ];

  paginationSubject$$ = new BehaviorSubject<{ currentPage: number; pageLimit: number }>({ currentPage: 1, pageLimit: 10 });
  pagination$ = this.paginationSubject$$.asObservable();

  sortingSubject$$ = new BehaviorSubject<string>('');
  sorting$ = this.sortingSubject$$.asObservable();

  tableData$ = combineLatest([
    this.filterForm.valueChanges.pipe(
      startWith(this.filterForm.getRawValue()),
      tap(() => {
        this.setFilterTags();
        this.paginationSubject$$.next({ ...this.paginationSubject$$.value, currentPage: 1 });
      }),
    ),
    this.pagination$,
    this.sorting$.pipe(
      tap(() => {
        this.paginationSubject$$.next({ ...this.paginationSubject$$.value, currentPage: 1 });
      }),
    ),
  ]).pipe(
    debounceTime(500),
    tap(() => (this.loading = true)),
    switchMap(([_, pagination, sorting]) => {
      const req = this.generateRequestFromFilters();
      if (!req.shopLocationCode) {
        this.loading = false;
        return of([] as EquipmentImportDetail[]);
      }
      // The API does not filter by category, so when one is selected filter and paginate here.
      const category = this.filterForm.getRawValue().category;
      const rows$ = category
        ? this.loadCategoryPage(req, sorting, category, pagination)
        : this.masterDataService
            .getEquipmentImportDetails(req, pagination.currentPage, pagination.pageLimit, sorting)
            .pipe(map((response) => response.body ?? []));
      return rows$.pipe(
        catchError((error) => {
          this.toaster.showToastr('Failed to load equipment import details', 'error');
          this.loading = false;
          return EMPTY;
        }),
      );
    }),
    map((rows) => {
      this.totalPagesCount = rows[0]?.totalCount ? Math.ceil(rows[0].totalCount / this.paginationSubject$$.value.pageLimit) : 1;
      return rows.map((item, index) => ({
        ...item,
        rowKey: `${this.paginationSubject$$.value.currentPage}-${index}`,
        category: this.getCategoryLabel(item),
      }));
    }),
    tap(() => (this.loading = false)),
  );

  containerGrades$ = this.masterDataService.getContainerGrades().pipe(
    map((data) => data.map((grade) => grade.containerGradeCode)),
    shareReplay(1),
  );

  containerType$ = of([
    'BULK',
    'CASC',
    'CARF',
    'CARP',
    'CLIP',
    'DRY',
    'FLAT',
    'FOLD',
    'GENS',
    'HCRF',
    'HIGH',
    'HIVE',
    'NOSE',
    'OPEN',
    'OSOT',
    'PLWD',
    'REEF',
    'RF',
    'TANK',
    'TWDK',
    'TWFL',
    'USLG',
  ]);

  private getCategoryLabel(item: EquipmentImportDetail): EquipmentCategoryLabel {
    if (item.isSales) return CATEGORY_LABELS.sales;
    if (item.isRedelivery) return CATEGORY_LABELS.redelivery;
    return CATEGORY_LABELS.normal;
  }

  /**
   * Returns one page of rows in the selected category. API pages are fetched one at a time, only until there are enough
   * matching rows for the requested page, and cached so revisiting pages with the same filters doesn't refetch.
   */
  private loadCategoryPage(
    req: EquipmentImportDetailsRequest,
    sorting: string,
    category: EquipmentCategory,
    pagination: { currentPage: number; pageLimit: number },
  ): Observable<EquipmentImportDetail[]> {
    const key = JSON.stringify({ req, sorting });
    if (this.categoryCache?.key !== key) {
      this.categoryCache = { key, rows: [], loadedApiPages: 0, apiTotalCount: Infinity };
    }
    const cache = this.categoryCache;
    const limit = InAdvanceContainerVisibilityComponent.MAX_API_PAGE_LIMIT;
    const start = (pagination.currentPage - 1) * pagination.pageLimit;
    const end = start + pagination.pageLimit;
    const matching = () => cache.rows.filter((row) => this.getCategoryLabel(row).toLowerCase() === category);
    const hasMore = () => cache.loadedApiPages * limit < cache.apiTotalCount;

    const fillPage = (): Observable<void> => {
      if (matching().length >= end || !hasMore()) return of(undefined);
      return this.masterDataService.getEquipmentImportDetails(req, cache.loadedApiPages + 1, limit, sorting).pipe(
        switchMap((response) => {
          const rows = response.body ?? [];
          cache.rows.push(...rows);
          cache.loadedApiPages++;
          cache.apiTotalCount = rows.length ? (rows[0].totalCount ?? 0) : 0;
          return fillPage();
        }),
      );
    };

    return fillPage().pipe(
      map(() => {
        const rows = matching();
        // Until every API page is loaded the total is unknown; report one extra row so the next page stays reachable.
        const totalCount = rows.length + (hasMore() ? 1 : 0);
        return rows.slice(start, end).map((row) => ({ ...row, totalCount }));
      }),
    );
  }

  categoryAppearance(category: string): 'success' | 'info' | 'warning' {
    if (category === CATEGORY_LABELS.sales) return 'warning';
    if (category === CATEGORY_LABELS.redelivery) return 'info';
    return 'success';
  }

  constructor() {}

  ngOnInit(): void {
    if (this.showShopLocationDropdown) {
      this.loadAllLocationCodesForDropdown();
    }
  }

  private loadAllLocationCodesForDropdown(): void {
    this.masterDataManagementService
      .getLocationCodes()
      .pipe(
        map((locations) => locations.map((location) => this.normalizeLocationCode(location.value)).filter(Boolean)),
        catchError(() => of([])),
      )
      .subscribe((locationCodes) => {
        const uniqueSortedCodes = Array.from(new Set([...(locationCodes ?? []), ...this.userShopLocations])).sort((a, b) =>
          a.localeCompare(b),
        );

        this.shopLocations = uniqueSortedCodes;
        this.updateVisibleShopLocations();
        const control = this.filterForm.get('shopLocationCode');
        const selectedCode = this.normalizeLocationCode(control?.value);
        if (selectedCode !== control?.value) {
          control?.setValue(selectedCode || null);
        }
      });
  }

  onLocationSearchInput(event: Event): void {
    const customEvent = event as CustomEvent;
    const target = event.target as HTMLInputElement | null;
    this.locationSearchTerm = `${customEvent.detail?.value ?? customEvent.detail ?? target?.value ?? ''}`.trim();
    this.updateVisibleShopLocations();
  }

  onLocationSelected(event: Event): void {
    const customEvent = event as CustomEvent;
    const target = event.target as HTMLInputElement | null;
    const selectedCode = this.normalizeLocationCode(`${customEvent.detail?.value ?? target?.value ?? ''}`);

    this.filterForm.get('shopLocationCode')?.setValue(selectedCode || null);
    if (selectedCode) {
      this.locationSearchTerm = selectedCode;
      this.updateVisibleShopLocations();
    }
  }

  private updateVisibleShopLocations(): void {
    const normalizedSearch = this.locationSearchTerm.toLowerCase();
    if (!normalizedSearch) {
      this.visibleShopLocations = this.mapLocationOptions(
        this.shopLocations.slice(0, InAdvanceContainerVisibilityComponent.INITIAL_RENDERED_LOCATION_OPTIONS),
      );
      return;
    }

    const matchingLocations = this.shopLocations.filter((location) => location.toLowerCase().includes(normalizedSearch));
    this.visibleShopLocations = this.mapLocationOptions(matchingLocations);
  }

  private mapLocationOptions(locationCodes: string[]): { label: string; value: string }[] {
    return locationCodes.map((locationCode) => ({
      label: locationCode,
      value: locationCode,
    }));
  }

  private normalizeLocationCode(locationCode: string | null | undefined): string {
    return `${locationCode ?? ''}`.trim().replace(/^[\\/]+/, '');
  }

  private resolveValidLocationCode(locationCode: string | null | undefined): string | undefined {
    const normalizedCode = this.normalizeLocationCode(locationCode);
    if (!normalizedCode) {
      return undefined;
    }

    const matchingLocation = this.shopLocations.find((shopLocation) => shopLocation.toLowerCase() === normalizedCode.toLowerCase());
    return matchingLocation;
  }

  setFilterTags() {
    this.filterTags = Object.keys(this.filterForm.value).reduce((tags: { key: string; value: string }[], key) => {
      if (this.filterForm.get(key)?.value) {
        const value = this.filterForm.get(key)?.value;
        if (key === 'currentWeek' && value) {
          tags.push({ key, value: `Week ${this.isoWeekDateRangeHelper.currentWeekNumber}` });
        } else if (key === 'week1' && value) {
          tags.push({ key, value: `Week ${this.isoWeekDateRangeHelper.week1Number}` });
        } else if (key === 'week2' && value) {
          tags.push({ key, value: `Week ${this.isoWeekDateRangeHelper.week2Number}` });
        } else if (typeof value === 'boolean' && value) {
          tags.push({ key, value: `${utils.camelToPascalCase(key)} : Yes` });
        } else if (key === 'shopLocationCode') {
          tags.push({ key, value: `Location : ${value}` });
        } else if (key === 'category') {
          tags.push({ key, value: `Category : ${CATEGORY_LABELS[value as EquipmentCategory]}` });
        } else {
          tags.push({ key, value: `${utils.camelToPascalCase(key)} : ${value}` });
        }
      }
      return tags;
    }, []);
  }

  private getSelectedDateRange(): { fromDateTime: string; toDateTime: string } {
    const form = this.filterForm.getRawValue();
    const weeks = [
      {
        selected: form.currentWeek,
        start: this.isoWeekDateRangeHelper.currentWeekStartUtc,
        end: this.isoWeekDateRangeHelper.currentWeekEndUtc,
      },
      { selected: form.week1, start: this.isoWeekDateRangeHelper.week1StartUtc, end: this.isoWeekDateRangeHelper.week1EndUtc },
      { selected: form.week2, start: this.isoWeekDateRangeHelper.week2StartUtc, end: this.isoWeekDateRangeHelper.week2EndUtc },
    ].filter((week) => week.selected);
    const weekStart = weeks[0]?.start ?? this.isoWeekDateRangeHelper.currentWeekStartUtc;
    return {
      // Never include past arrivals: the current week starts on Monday, which may be before today.
      fromDateTime: new Date(Math.max(Date.parse(weekStart), Date.parse(this.isoWeekDateRangeHelper.todayStartUtc))).toISOString(),
      toDateTime: weeks.at(-1)?.end ?? this.isoWeekDateRangeHelper.currentWeekEndUtc,
    };
  }

  private generateRequestFromFilters(): EquipmentImportDetailsRequest {
    const form = this.filterForm.getRawValue();
    return {
      dateRange: this.getSelectedDateRange(),
      shopLocationCode: this.resolveValidLocationCode(form.shopLocationCode),
      containerNumber: form.containerNumber ?? undefined,
      equipmentType: form.type ?? undefined,
      equipmentSize: form.size ?? undefined,
      isSales: form.category === 'sales' || undefined,
      isRedelivery: form.category === 'redelivery' || undefined,
      isNormal: form.category === 'normal' || undefined,
      gradeCode: form.grade ?? undefined,
    };
  }

  onCheckboxChange(controlName: string, event: Event): void {
    const checked = (event as CustomEvent).detail?.checked ?? (event.target as HTMLInputElement).checked;
    if (['currentWeek', 'week1', 'week2'].includes(controlName)) {
      const otherWeeks = ['currentWeek', 'week1', 'week2'].filter((name) => name !== controlName);
      if (!checked && !otherWeeks.some((name) => this.filterForm.get(name)?.value)) {
        event.preventDefault();
        event.stopImmediatePropagation();
        return;
      }
      if (checked && controlName === 'week2' && this.filterForm.get('currentWeek')?.value) {
        this.filterForm.get('week1')?.setValue(true);
      }
      if (checked && controlName === 'currentWeek' && this.filterForm.get('week2')?.value) {
        this.filterForm.get('week1')?.setValue(true);
      }
      if (!checked && controlName === 'week1' && this.filterForm.get('currentWeek')?.value && this.filterForm.get('week2')?.value) {
        this.filterForm.get('week2')?.setValue(false);
      }
    }
    this.filterForm.get(controlName)?.setValue(checked);
  }

  onPageLimitChange(event: Event): void {
    const customEvent = event as CustomEvent;
    this.paginationSubject$$.next({ currentPage: 1, pageLimit: customEvent.detail });
  }

  onPageChange(event: Event): void {
    const customEvent = event as CustomEvent;
    const changedPage = customEvent.detail as number;
    const currentPageLimit = this.paginationSubject$$.value.pageLimit;
    if (changedPage !== this.paginationSubject$$.value.currentPage) {
      this.paginationSubject$$.next({ currentPage: changedPage, pageLimit: currentPageLimit });
    }
  }

  onSortChange(event: Event): void {
    const customEvent = event as CustomEvent;
    const { column, direction } = customEvent.detail;
    let dir = '';
    if (direction === 'ascending') dir = 'asc';
    else if (direction === 'descending') dir = 'desc';
    this.currentSortColumn = column;
    this.currentSortDirection = direction;
    this.sortingSubject$$.next(`${column}:${dir}`);
  }

  async onDownloadClick(event: Event): Promise<void> {
    this.isDownloadingData = true;
    const blob = await lastValueFrom(
      this.masterDataService
        .downloadEquipmentImportDetails({
          columns: [],
          filters: this.buildDownloadFilters(),
        })
        .pipe(
          catchError((error) => {
            this.toaster.showToastr('Failed to download data', 'error');
            return of(null);
          }),
        ),
    );

    if (blob) downloadBlob(blob, generateDateFilename('equipment_import_details', 'xlsx'));
    this.isDownloadingData = false;
  }

  isFilterActive(controlName: string): boolean {
    const ctrl = this.filterForm.get(controlName);
    if (!ctrl) return false;
    const val = ctrl.value;
    return !!val && !(Array.isArray(val) && val.length === 0);
  }

  canClearFilter(controlName: string): boolean {
    if (controlName === 'shopLocationCode') return false;
    if (!['currentWeek', 'week1', 'week2'].includes(controlName)) return true;
    return ['currentWeek', 'week1', 'week2'].some((week) => week !== controlName && this.filterForm.get(week)?.value);
  }

  clearFilter(controlName: string): void {
    const ctrl = this.filterForm.get(controlName);
    if (!ctrl || !this.canClearFilter(controlName)) return;
    if (controlName === 'week1' && this.filterForm.get('currentWeek')?.value && this.filterForm.get('week2')?.value) {
      this.filterForm.get('week2')?.setValue(false);
    }
    ctrl.setValue(null);
  }

  clearAllFilters(): void {
    ['containerNumber', 'grade', 'size', 'type', 'category'].forEach((c) => {
      const ctrl = this.filterForm.get(c);
      if (ctrl) ctrl.setValue(null);
    });
    this.filterForm.get('currentWeek')?.setValue(true);
    this.filterForm.get('week1')?.setValue(false);
    this.filterForm.get('week2')?.setValue(false);
  }

  private buildDownloadFilters(): DownloadFilterCriteria[] {
    const filters: DownloadFilterCriteria[] = [];
    const form = this.filterForm.getRawValue();
    if (form.shopLocationCode) {
      filters.push(toEqualsFilter('shop_location_code', form.shopLocationCode));
    }
    if (form.containerNumber) {
      filters.push(toEqualsFilter('equipment_number', form.containerNumber));
    }
    if (form.type) {
      filters.push(toEqualsFilter('equipment_type', form.type));
    }
    if (form.size) {
      filters.push(toEqualsFilter('equipment_size', `${form.size}`));
    }
    if (form.grade) {
      filters.push(toEqualsFilter('grade_code', form.grade));
    }
    const categoryColumn = { sales: 'is_sales', redelivery: 'is_redelivery', normal: 'is_normal' } as const;
    if (form.category) filters.push(toEqualsFilter(categoryColumn[form.category], 'true'));
    const { fromDateTime, toDateTime } = this.getSelectedDateRange();
    filters.push(...toDateBetweenFilters('estimated_time_of_arrival_depot', fromDateTime, toDateTime));
    return filters;
  }
}
